import { SectionHeader, SeeAllButton } from "@/components/marketplace/layout/section-header"
import { isKnownSectionType } from "@/features/feed/contract"
import type { FeedPage, FeedResponseSection, FeedSection, FormFactor } from "@/features/feed/contract"
import { isStandaloneSectionType } from "@/features/feed/contract/section-types"
import { FeedAnomalyLog, type FeedAnomalyEntry } from "@/features/feed/FeedAnomalyLog"
import { FeedRow } from "@/features/feed/FeedRow"
import { rendersNothing, sectionComponentFor } from "@/features/feed/registry"
import { resolveFeedLink } from "@/features/feed/resolve-link"
import { knownMembers, rowAnomalyFor, rowWeightFor, sectionIsFullBleed, segmentUnits, showsOn, unitIsFullBleed, unitShowsOn } from "@/features/feed/section-layout"
import type { UnitMember } from "@/features/feed/section-layout"
import { feedHeaderSize } from "@/features/feed/sections/SectionFrame"
import { SectionShell } from "@/features/feed/SectionShell"
import { itemAt } from "@/lib/helpers/array"

// A full-bleed band normally ends in its own padding, so the next unit sits flush
// against it. These two end on a hard edge instead — the hero's dark stage, and the
// cover's photograph (or, below @5xl, the button stacked under it) — and content has
// to lift off that edge, so whatever follows them keeps its flow gap.
const EDGE_BOTTOMED_TYPES = new Set<FeedResponseSection["type"]>(["HERO_CAROUSEL", "HERO_PHOTO", "CATEGORY_COVER"])
const unitBottomsOnAnEdge = (unit: UnitMember[]) => unit.length === 1 && EDGE_BOTTOMED_TYPES.has(itemAt(unit, 0).section.type)

// Consecutive same-background bands collapse to one boundary — but only when
// the previous band is visible on every form factor the current one shows on.
const collapsesInto = (previous: FeedResponseSection | undefined, section: FeedSection): boolean => {
  if (!previous) return false
  const { background, formFactors } = section.presentation
  if (background === "none" || previous.presentation.background !== background) return false
  return formFactors.every((formFactor) => previous.presentation.formFactors.includes(formFactor))
}

// A unit needs the flow gap on a form factor unless it (or its nearest
// visible-on-that-form-factor predecessor) is a full-bleed band whose own
// padding is the boundary. Hidden siblings are skipped — CSS adjacency can't.
// At the top of the page the chrome is the predecessor: the first visible
// unit breathes off the navbar, full-bleed bands touch it.
type FlowGap = "none" | "chrome" | "flow"

const gapOn = (units: UnitMember[][], unitIndex: number, previousSection: FeedResponseSection | undefined, formFactor: FormFactor): FlowGap => {
  const unit = itemAt(units, unitIndex)
  if (!unitShowsOn(unit, formFactor) || unitIsFullBleed(unit)) return "none"
  for (let j = unitIndex - 1; j >= 0; j--) {
    const earlier = itemAt(units, j)
    if (unitShowsOn(earlier, formFactor)) return unitBottomsOnAnEdge(earlier) || !unitIsFullBleed(earlier) ? "flow" : "none"
  }
  if (!previousSection) return "chrome"
  // Beyond one section back the previous batch is unknown — prefer a gap over a collision.
  if (!showsOn(previousSection, formFactor)) return "flow"
  return EDGE_BOTTOMED_TYPES.has(previousSection.type) || !sectionIsFullBleed(previousSection) ? "flow" : "none"
}

const GAP_CLASS: Record<Exclude<FlowGap, "none">, string> = { chrome: "mt-chrome", flow: "mt-flow" }

const flowGapClass = (units: UnitMember[][], unitIndex: number, previousSection: FeedResponseSection | undefined): string => {
  const mobile = gapOn(units, unitIndex, previousSection, "MOBILE")
  const desktop = gapOn(units, unitIndex, previousSection, "DESKTOP")
  if (mobile === desktop) return mobile === "none" ? "" : GAP_CLASS[mobile]
  const base = mobile === "none" ? "" : GAP_CLASS[mobile]
  const wide = desktop === "none" ? "@3xl:mt-0" : `@3xl:${GAP_CLASS[desktop]}`
  return [base, wide].filter(Boolean).join(" ")
}

interface FeedSectionsProps {
  page: FeedPage
  sections: FeedResponseSection[]
  countryCode: string
  // Position of the first section within the whole feed — scroll pages keep counting.
  startIndex?: number
  // The section just before this batch, so flushTop adjacency holds across
  // the SSR/scroll boundary.
  previousSection?: FeedResponseSection
  track?: boolean
}

const anomaliesFor = (sections: FeedResponseSection[], units: UnitMember[][]): FeedAnomalyEntry[] => {
  const unknown = sections
    .filter((section) => !isKnownSectionType(section.type))
    .map((section) => ({ event: "feed.section.skipped_unknown_type", sectionId: section.id, sectionType: section.type }))
  const rows = units.flatMap((unit) =>
    unit.flatMap(({ section }) => {
      const anomaly = rowAnomalyFor(section, unit)
      return anomaly ? [{ event: `feed.section.row_${anomaly}`, sectionId: section.id, sectionType: section.type }] : []
    })
  )
  return [...unknown, ...rows]
}

// Adapters that draw their own SectionHeader from presentation.title instead of letting
// SectionFrame do it. Listed rather than inferred because only the adapter knows, and for
// COLLECTION_LINKS it turns on the variant.
const ownsItsHeader = (section: FeedSection): boolean => {
  if (isStandaloneSectionType(section.type)) return true
  switch (section.type) {
    case "HERO_CAROUSEL":
    case "HERO_PHOTO":
    case "DROP_RAIL":
    case "GIFTS_BY_PRICE":
    case "PRODUCT_CAROUSEL":
    case "CATEGORY_COVER":
      return true
    case "COLLECTION_LINKS":
      return section.presentation.variant === "banners"
    default:
      return false
  }
}

const PRELOADS_IMAGERY = new Set<FeedSection["type"]>([
  "SPLIT_HERO",
  "PHOTO_HERO",
  "PHOTO_BANNER",
  "TRUST_PANEL",
  "BRAND_SPOTLIGHT",
  "LOOK_CARD",
  "OUTFIT_ROW",
  "CATEGORY_HERO",
  "PRODUCT_SPOTLIGHT",
  "HERO_CAROUSEL",
  "HERO_PHOTO",
  "SPOTLIGHT",
  "BRAND_SPOTLIGHT_BAND",
  "CATEGORY_COVER",
  "LOOK_ROW",
  "COLLECTION_LINKS",
])

const leadSectionIndex = (sections: Pick<FeedSection, "type">[]): number =>
  sections.findIndex((section) => PRELOADS_IMAGERY.has(section.type) && Boolean(sectionComponentFor(section.type)) && !rendersNothing(section.type))

export const FeedSections = ({ page, sections, countryCode, startIndex = 0, previousSection, track = true }: FeedSectionsProps) => {
  const known = knownMembers(sections)
  const units = segmentUnits(known.map(({ section }) => section))
  const anomalies = anomaliesFor(sections, units)

  const leadIndex = leadSectionIndex(known.map(({ section }) => section))

  const renderShell = (member: UnitMember, inRow: boolean, flowClass: string) => {
    const { section, index } = member
    const Renderer = sectionComponentFor(section.type)
    if (!Renderer || rendersNothing(section.type)) return null
    const position = startIndex + itemAt(known, index).payloadIndex
    const flushTop = collapsesInto(index === 0 ? previousSection : itemAt(known, index - 1).section, section)
    return (
      <SectionShell key={section.id} section={section} page={page} position={position} fullBleed={sectionIsFullBleed(section)} flowClass={flowClass} inRow={inRow} track={track}>
        <Renderer section={section} context={{ countryCode, flushTop, page, position, isLeadSection: startIndex === 0 && index === leadIndex, inRow }} />
      </SectionShell>
    )
  }

  return (
    <>
      {units.map((unit, unitIndex) => {
        const flowClass = flowGapClass(units, unitIndex, previousSection)
        if (unit.length === 1) {
          return renderShell(itemAt(unit, 0), false, flowClass)
        }
        // The row sits inside a container of its own. `@5xl:grid` is a CONTAINER query and an
        // element cannot query itself, so the declaration goes on a wrapper — and nothing above
        // it declared one either — page chrome, main and body are all container-type
        // normal — so the query could never match and every row stayed a vertical stack at
        // every width. Measured 2026-08-26: a four-member row at 1440 rendered four
        // full-width cards. Querying itself is also what the breakpoint means here: side by
        // side once the row itself is wide enough to hold the tracks.
        //
        // Below that width the row is a reel, not a stack. The members are already children
        // of one element, so scroll-snap works across them — what a stack cannot do is let
        // four cards read as one horizontal set on a phone. `reel` carries the axis lock and
        // the touch-action that keeps a vertical swipe scrolling the page instead of dead-
        // locking on the strip.
        //
        // The gap changes meaning with the axis: 12px between cards travelling on a phone,
        // 20px between tracks of one object on desktop, which is what the design draws.
        const gridTemplateColumns = unit.map((member) => `minmax(0, ${rowWeightFor(member.section)}fr)`).join(" ")
        // One header for the row, taken from the first member that carries a title. A member
        // renders inside its own grid track, so a title left on each would print four
        // headings across the row instead of one above it — hence `inRow` on the context,
        // which is how SectionFrame knows to stand down.
        // Hoisting is deliberately narrow, because two things can go wrong with it.
        //
        // A section whose adapter passes `ownsHeader` draws the heading itself, inside its
        // own track — `inRow` stands SectionFrame down but cannot reach the component. Hoist
        // a title out of one of those and the row prints it twice. Only the adapter knows,
        // and for COLLECTION_LINKS it depends on the variant, so the predicate lives beside
        // the adapters and has to stay in step with them.
        //
        // And a title is a member's own copy: silently dropping the other three to label the
        // row with the first one's is worse than showing all four. So the row hoists only
        // when exactly one member offers a title. Past that, every member keeps its own
        // header where it always rendered — visibly wrong rather than quietly lossy, which
        // is the version an author can act on.
        const hoistable = unit.filter((member) => member.section.presentation.title && !ownsItsHeader(member.section))
        const rowTitle = hoistable.length === 1 ? itemAt(hoistable, 0).section.presentation.title : undefined
        const rowSeeAll = rowTitle?.seeAll ? resolveFeedLink(rowTitle.seeAll, countryCode, page) : undefined
        return (
          <div key={itemAt(unit, 0).section.id} data-feed-row={itemAt(unit, 0).section.id} className={`max-w-page @container mx-auto w-full ${flowClass}`}>
            {rowTitle && (
              <SectionHeader
                title={rowTitle.text}
                accent={rowTitle.accent}
                eyebrow={rowTitle.eyebrow}
                size={feedHeaderSize(rowTitle.style) ?? "default"}
                numeral={rowTitle.numeral}
                action={rowSeeAll ? <SeeAllButton href={rowSeeAll} /> : null}
                className="px-gut mb-300"
              />
            )}
            <FeedRow count={unit.length} gridTemplateColumns={gridTemplateColumns}>
              {unit.map((member) => renderShell(member, rowTitle !== undefined, ""))}
            </FeedRow>
          </div>
        )
      })}
      {anomalies.length > 0 && <FeedAnomalyLog entries={anomalies} pageHandle={page.handle} />}
    </>
  )
}
